import React, { PureComponent } from 'react';

import { IntlProvider as IntlNextProvider } from 'react-intl';

import { logException } from '../../utils/sentry';
import { DEFAULT_LOCALE, getEffectiveLocale, loadIntlData } from './i18n-helper';
import { type I18nProviderProps, type I18nProviderState } from './type';

/**
 * Sets up the react-intl IntlProvider for its children, blocking until translations are loaded
 */

const NO_MESSAGES = {};

// eslint-disable-next-line @repo/internal/react/no-class-components
export default class I18nProvider extends PureComponent<I18nProviderProps, I18nProviderState> {
	state: I18nProviderState = {
		locale: DEFAULT_LOCALE,
		messages: undefined,
	};

	componentDidMount(): void {
		if (this.props.skip !== true) {
			void this.loadData();
		}
	}

	async loadData(): Promise<void> {
		const { messagesConfig, locale: predefinedLocale } = this.props;
		const locale = await getEffectiveLocale(predefinedLocale);

		if (locale === DEFAULT_LOCALE) {
			// Everything is already loaded for English
			this.setState({
				locale: DEFAULT_LOCALE,
				messages: {},
			});
			return;
		}

		try {
			const messages = await loadIntlData(messagesConfig, locale);

			this.setState({
				locale,
				messages: messages || NO_MESSAGES,
			});
		} catch (error) {
			logException(error, 'Error loading translations.', { locale });
			// show english on error
			this.setState({
				locale: DEFAULT_LOCALE,
				messages: NO_MESSAGES,
			});
		}
	}

	render():
		| string
		| number
		| boolean
		| React.JSX.Element
		| Iterable<React.ReactNode>
		| null
		| undefined {
		const { skip } = this.props;

		// TODO: [PTC-4968] Long term 'skip' prop to be removed in favour being skipped by
		//       default and `embeddable-directory` to rely on parent product i18n provider.
		//       The idea is to merge package i18n files into product ones, and ship that
		//       one global product i18n translation rather than packages fetching their own.
		if (skip) {
			return this.props.children;
		}

		const { locale, messages } = this.state;

		// Idea for improvement we should not block rendering UI while fetching messages
		// https://product-fabric.atlassian.net/browse/PTC-2176
		if (!messages) {
			return null;
		}

		return (
			<IntlNextProvider
				key={`intl-provider-5-${locale}`}
				data-test-language={locale}
				locale={locale}
				defaultLocale={DEFAULT_LOCALE}
				messages={messages}
			>
				{this.props.children}
			</IntlNextProvider>
		);
	}
}
